import React, { ReactElement, ReactNode, useCallback } from 'react'
import { matchPath, useHistory, useLocation } from 'react-router-dom'

import { makeStyles } from '@material-ui/core/styles'

import { Tab, Tabs } from '@material-ui/core'

import { NonEmptyArrayReadOnly } from '../common/tsUtils'

export type RouteTabItem = {
  title: string

  isDisabled?: boolean

  path: string

  /**
   * Must not dereference observable state in this lambda. Use hooks inside the component instead.
   */
  render: () => ReactNode

  template?: string
}

export type RouteTabsProps = {
  /**
   * Must be a single Switch component.
   */
  children: ReactElement

  routeTabs: NonEmptyArrayReadOnly<RouteTabItem>
}

/**
 * Consider using {@link RoutingTabsSwitch} instead.
 */
export const RoutingTabs =
  ({routeTabs, children}: RouteTabsProps) => {
    const classes = useStyles()

    const history = useHistory()
    const location = useLocation()

    const activeTabIndex =
      Math.max(0,
        routeTabs.findIndex(route =>
          matchPath(location.pathname, {
            path: route.template ?? route.path,
            // Must not be exact for possible deeper routing
            exact: false,
            strict: false
          })
        ))

    const onTabChange = useCallback(
      (_: unknown, newActiveTabIndex: number) => {
        if (newActiveTabIndex < 0 || newActiveTabIndex >= routeTabs.length) {
          throw new Error(`Bad new tab index: ${newActiveTabIndex} vs ${routeTabs.length}`)
        }

        if (newActiveTabIndex === activeTabIndex) {
          return
        }

        const {path, isDisabled} = routeTabs[newActiveTabIndex]

        if (!isDisabled) {
          history.push(path)
        }
      },
      [routeTabs, history, activeTabIndex])

    return <>
      <div className={classes.tabsWrap}>
        <Tabs
          value={activeTabIndex}
          onChange={onTabChange}
          className={classes.tabs}
          TabIndicatorProps={{hidden: true}}
        >
          {
            routeTabs.map((route, index) =>
              <Tab
                disableRipple
                key={index}
                label={<p>{route.title}</p>}
                disabled={route.isDisabled}
              />)
          }
        </Tabs>
      </div>

      {children}
    </>
  }

const useStyles =
  makeStyles(
    ({palette}) => ({
      tabsWrap: {
        width: '100%',
        paddingTop: '20px',
        paddingBottom: '20px'
      },
      tabs: {
        maxWidth: '100%',
        minWidth: 0,
        '& button': {
          maxWidth: '100px'
        },
        '& p': {
          fontSize: 12,
          fontWeight: 500,
          color: palette.mpSkladPrimary.gray
        },
        '& .Mui-selected p': {
          color: palette.mpSkladPrimary.blue,
          fontSize: 12,
          fontWeight: 600,
          borderBottom: '2px solid'
        },
        '& .MuiTab-root': {
          padding: 0,
          minWidth: 'fit-content',
          marginRight: 40,
          overflow: 'unset'
        }
      }
    }))